import { ReactNode, useCallback, useMemo } from 'react';
import { PropertiesContext } from './PropertiesContextObject';
import { Property, PropertyRow } from '../types';
import { supabase } from '@cleanpro/common-ui';
import { useFetchProperties } from '../hooks/useFetchProperties';
import { transformPropertyRow } from '../api';

interface PropertiesDataProviderProps {
  children: ReactNode;
}

export const PropertiesDataProvider = ({ children }: PropertiesDataProviderProps) => {
  const { properties, setProperties, loading, error } = useFetchProperties();

  const addProperty = useCallback(async (property: Omit<Property, 'id' | 'createdAt' | 'updatedAt'>) => {
    console.log('Attempting to add property:', property);
    const { data, error } = await supabase
        .from('properties')
        .insert({
            name: property.name,
            customer_id: property.customerId,
            property_type: property.propertyType,
            address_line_1: property.addressLine1,
            address_line_2: property.addressLine2,
            city: property.city,
            state: property.state,
            postal_code: property.postalCode,
            square_footage: property.squareFootage,
            bedrooms: property.bedrooms,
            bathrooms: property.bathrooms,
            floors: property.floors,
            access_instructions: property.accessInstructions,
            special_instructions: property.specialInstructions,
            has_pets: property.hasPets,
            pet_details: property.petDetails,
            preferred_cleaning_day: property.preferredCleaningDay,
            requested_time_slot: property.requestedTimeSlot,
            is_active: property.isActive,
        })
        .select()
        .single();

    if (error) {
        console.error('Error adding property in context:', error);
        throw error;
    }
    
    console.log('Property added successfully to database:', data);
    const newProperty = transformPropertyRow(data as PropertyRow);
    console.log('Transformed new property:', newProperty);
    setProperties((prev) => [...prev, newProperty]);
  }, [setProperties]);

  const removeProperty = useCallback(async (propertyId: string) => {
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);

      if (error) throw error;

      setProperties((prev) => prev.filter(p => p.id !== propertyId));
    } catch (err) {
      console.error('Error removing property:', err);
      throw err;
    }
  }, [setProperties]);

  // Helper function to map Property fields to database columns
  const mapPropertyToUpdateData = (propertyData: Partial<Omit<Property, 'id'>>): Record<string, unknown> => {
    const fieldMapping: Record<string, string> = {
      name: 'name',
      customerId: 'customer_id',
      propertyType: 'property_type',
      addressLine1: 'address_line_1',
      addressLine2: 'address_line_2',
      city: 'city',
      state: 'state',
      postalCode: 'postal_code',
      squareFootage: 'square_footage',
      bedrooms: 'bedrooms',
      bathrooms: 'bathrooms',
      floors: 'floors',
      accessInstructions: 'access_instructions',
      specialInstructions: 'special_instructions',
      hasPets: 'has_pets',
      petDetails: 'pet_details',
      preferredCleaningDay: 'preferred_cleaning_day',
      requestedTimeSlot: 'requested_time_slot',
      isActive: 'is_active'
    };

    const updateData: Record<string, unknown> = {};
    
    for (const [camelKey, snakeKey] of Object.entries(fieldMapping)) {
      if (camelKey in propertyData) {
        updateData[snakeKey] = propertyData[camelKey as keyof typeof propertyData];
      }
    }
    
    return updateData;
  };

  const updateProperty = useCallback(async (propertyId: string, propertyData: Partial<Omit<Property, 'id'>>) => {
    try {
      const updateData = mapPropertyToUpdateData(propertyData);

      const { data, error } = await supabase
        .from('properties')
        .update(updateData)
        .eq('id', propertyId)
        .select()
        .single();

      if (error) throw error;

      const updatedProperty = transformPropertyRow(data as PropertyRow);
      setProperties(prev =>
        prev.map(p =>
          p.id === propertyId ? { ...p, ...updatedProperty } : p
        )
      );
    } catch (err) {
      console.error('Error updating property:', err);
      throw err;
    }
  }, [setProperties]);

  const value = useMemo(() => ({
    properties,
    loading,
    error,
    addProperty,
    removeProperty,
    updateProperty
  }), [properties, loading, error, addProperty, removeProperty, updateProperty]);

  return (
    <PropertiesContext.Provider value={value}>
      {children}
    </PropertiesContext.Provider>
  );
};
